import React, { useState, useEffect } from 'react';
import { X, ChevronUp, ChevronDown } from 'lucide-react';
import Swal from 'sweetalert2';
import {
    getCreditNoteDetailById,
    getApprovalEntitySetting,
    getApprovalHistory,
    saveApprovedData
} from '../../utils/lead';
import { getSession } from '../../getSession';
import './ShowCreditNoteView.css';

const isSentForApproval = (status) => {
    const s = (status || '').toLowerCase().replace(/\s+/g, '');
    return s.includes('approval') || s === 'sentforapproval';
};

const ShowCreditNoteView = ({ creditNoteId, onBack, onRefresh }) => {
    const [invoice, setInvoice]           = useState(null);
    const [isLoading, setIsLoading]       = useState(true);
    const [isHistoryOpen, setIsHistoryOpen] = useState(true);

    // Approval state
    const [approvalDetail, setApprovalDetail]   = useState(null);
    const [approvalHistory, setApprovalHistory] = useState([]);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectStatus, setRejectStatus]       = useState('Rejected');
    const [rejectRemarks, setRejectRemarks]     = useState('');
    const [savingApproval, setSavingApproval]   = useState(false);

    const { userId } = getSession();

    /* ── Fetch invoice + approval data ── */
    useEffect(() => {
        const fetchDetail = async () => {
            setIsLoading(true);
            try {
                const data = await getCreditNoteDetailById({
                    Id: creditNoteId,
                    TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                });
                setInvoice(data);

                if (data && isSentForApproval(data.status)) {
                    fetchApprovalData(creditNoteId);
                }
            } catch (err) {
                console.error('ShowCreditNoteView fetch error:', err);
            } finally {
                setIsLoading(false);
            }
        };
        if (creditNoteId) fetchDetail();
    }, [creditNoteId]);

    const fetchApprovalData = async (id) => {
        try {
            const [detail, history] = await Promise.all([
                getApprovalEntitySetting({ UserId: userId, EntityName: 'CreditNote', EntityId: id }),
                getApprovalHistory({ UserId: userId, EntityId: id, EntityName: 'CreditNote' })
            ]);
            setApprovalDetail(detail || null);
            setApprovalHistory(Array.isArray(history) ? history : []);
        } catch (err) {
            console.error('Approval data fetch error:', err);
        }
    };

    /* ── Approve handler ── */
    const handleApprove = async () => {
        if (!approvalDetail) return;
        const result = await Swal.fire({
            title: '',
            text: 'Are you sure, you want to approve this credit note?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'Cancel',
        });
        if (!result.isConfirmed) return;

        setSavingApproval(true);
        try {
            const response = await saveApprovedData({
                UserId: userId,
                invoiceApprovalData: {
                    ApprovalId: approvalDetail.Id,
                    EntityId: creditNoteId,
                    Status: 'Approved',
                    Remark: '',
                    IsActive: true
                }
            });
            if (response) {
                Swal.fire('Success', 'Credit Note approved successfully', 'success');
                if (onRefresh) onRefresh();
                onBack();
            } else {
                Swal.fire('Oops', 'Unable to process approval. Please contact support.', 'warning');
            }
        } catch {
            Swal.fire('Error', 'Unable to process approval.', 'error');
        } finally {
            setSavingApproval(false);
        }
    };

    /* ── Save Approve/Reject from modal ── */
    const handleSaveModal = async () => {
        if (!approvalDetail) return;
        const isApprove = rejectStatus === 'Approved';

        const result = await Swal.fire({
            title: '',
            text: `Are you sure, you want to ${isApprove ? 'approve' : 'reject'} this credit note?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'Cancel',
        });
        if (!result.isConfirmed) {
            Swal.fire(`${isApprove ? 'Approval' : 'Rejection'} Cancelled`, '', 'info');
            return;
        }

        setSavingApproval(true);
        try {
            const response = await saveApprovedData({
                UserId: userId,
                invoiceApprovalData: {
                    ApprovalId: approvalDetail.Id,
                    EntityId: creditNoteId,
                    Status: rejectStatus,
                    Remark: rejectRemarks,
                    IsActive: isApprove
                }
            });
            if (response) {
                setShowRejectModal(false);
                Swal.fire('Success', `Credit Note ${rejectStatus.toLowerCase()} successfully`, 'success');
                if (onRefresh) onRefresh();
                onBack();
            } else {
                Swal.fire('Oops', 'Unable to process your request. Please contact support.', 'warning');
            }
        } catch {
            Swal.fire('Error', 'Unable to process your request.', 'error');
        } finally {
            setSavingApproval(false);
        }
    };

    /* ── Helpers ── */
    const getStatusClass = (status = '') => {
        const s = status.toLowerCase();
        if (s === 'open') return 'badge-primary';
        if (['sent', 'accepted', 'approved', 'refunded'].includes(s)) return 'badge-success';
        if (s === 'edit') return 'badge-grey';
        if (['cancelled', 'rejected'].includes(s)) return 'badge-danger';
        if (isSentForApproval(s)) return 'badge-warning';
        return 'badge-primary';
    };

    const fmt = (n) =>
        new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

    /* ── Guards ── */
    if (isLoading) return (
        <div className="shw-cn-loading">
            <div className="shw-spinner" />
            <span>Loading...</span>
        </div>
    );
    if (!invoice) return <div className="shw-cn-error">Credit note details not found.</div>;

    const items        = invoice.creditNoteItems || [];
    const histories    = invoice.creditNoteHistories || [];
    const otherCharges = invoice.invoiceOtherCharges || invoice.creditNoteOtherCharges || [];
    const subTotal     = items.reduce((acc, item) => acc + (parseFloat(item.Total) || 0), 0);
    const otherTotal   = otherCharges.reduce((acc, c) => acc + (parseFloat(c.Amount) || 0), 0);
    const grandTotal   = subTotal + otherTotal;
    const cur          = invoice.CurrencySymbol || '₹';

    const showApprovalSection = isSentForApproval(invoice.status);
    const hasApprover         = approvalDetail && approvalDetail.Id > 0;

    return (
        <div className="shw-cn-view">

            {/* ── Header ── */}
            <div className="shw-cn-header">
                <button className="shw-back-btn" onClick={onBack}>
                    <X size={16} />
                    <span>Back to Credit Note</span>
                </button>
            </div>

            {/* ── Lead Info Bar ── */}
            <div className="shw-lead-bar">
                <div className="shw-lead-left">
                    <img
                        src={invoice.leadDetail?.ProfileImgFileName || '/assets/custom/img/100_13.jpg'}
                        alt="Lead"
                        className="shw-lead-avatar"
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="shw-lead-text">
                        <div className="shw-lead-name">{invoice.leadDetail?.PersonName || invoice.contact_name}</div>
                        <div className="shw-lead-phone">{invoice.leadDetail?.MobileNo}</div>
                        <div className="shw-lead-no">Lead No. {invoice.leadDetail?.LeadNo}</div>
                    </div>
                </div>
                <div className="shw-cn-ref">
                    Credit Note: <strong>{invoice.credit_note_number}</strong>
                </div>
            </div>

            {/* ── Summary Bar ── */}
            <div className="shw-summary-bar">
                <div className="shw-summary-item">
                    <label>Status</label>
                    <span className={`badge badge-pill ${getStatusClass(invoice.status)}`}>{invoice.status}</span>
                </div>
                <div className="shw-summary-item">
                    <label>Customer</label>
                    <span>{invoice.contact_name}</span>
                </div>
                <div className="shw-summary-item">
                    <label>Amount</label>
                    <span><strong>{invoice.amount}</strong></span>
                </div>
                <div className="shw-summary-item">
                    <label className="shw-label-yellow">Valid Till</label>
                    <span>{invoice.issued_at}</span>
                </div>
            </div>

            {/* ── APPROVAL SECTION (only when Sent For Approval) ── */}
            {showApprovalSection && (
                <div className="apv-detail-box">

                    {/* Status Banner */}
                    <div className="apv-invoicestatus">

                        {/* Wait For Approval – shown when the viewer is not the approver */}
                        {!hasApprover && (
                            <div className="apv-wait-banner">
                                <div className="apv-icon-wrap">
                                    <img
                                        src="/assets/custom/img/waitingApproval.png"
                                        alt="Waiting"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                </div>
                                <h4>Wait For Approval</h4>
                                <p>
                                    This Credit Note has been submitted for approval. If you have enabled
                                    notification, you'll be notified once it's approved.
                                </p>
                            </div>
                        )}

                        {/* Approve / Reject – shown to the designated approver */}
                        {hasApprover && (
                            <div className="apv-approve-banner">
                                <div className="apv-approve-header">
                                    <div className="apv-icon-wrap-inline">
                                        <img
                                            src="/assets/custom/img/waitingApproval.png"
                                            alt="Approval"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    </div>
                                    <h4>Approve the Credit Note</h4>
                                    <div className="apv-action-btns">
                                        <button
                                            className="btn btn-success btn-sm"
                                            onClick={handleApprove}
                                            disabled={savingApproval}
                                        >
                                            Approve
                                        </button>
                                        <button
                                            className="btn btn-danger btn-sm ml-1"
                                            onClick={() => {
                                                setRejectStatus('Rejected');
                                                setRejectRemarks('');
                                                setShowRejectModal(true);
                                            }}
                                            disabled={savingApproval}
                                        >
                                            Reject
                                        </button>
                                    </div>
                                </div>
                                <p>This Credit Note has been submitted for approval. Verify and approve the Credit Note.</p>
                            </div>
                        )}
                    </div>

                    {/* Approvers Box */}
                    {hasApprover && (
                        <div className="apv-approvers-box">
                            <div className="apv-approvers-header">
                                <h4>
                                    <span>Approvers</span>
                                    <a
                                        href="#"
                                        className="apv-history-link"
                                        onClick={(e) => e.preventDefault()}
                                    >
                                        Approval History
                                    </a>
                                </h4>
                            </div>

                            {/* Approver Card */}
                            <div className="apv-user-detail">
                                <div className="apv-user-card">
                                    <img
                                        src="/assets/custom/img/100_13.jpg"
                                        alt={approvalDetail.User}
                                        className="apv-user-avatar"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                    <h5 className="apv-user-name">{approvalDetail.User}</h5>
                                    <div className="apv-tooltip">
                                        <h5>{approvalDetail.User}</h5>
                                        <p>{approvalDetail.Email}</p>
                                        <p className="apv-status-pill">Pending</p>
                                    </div>
                                </div>
                            </div>

                            {/* Approval History Rows */}
                            {approvalHistory.length > 0 && (
                                <div className="apv-history-list">
                                    {/* Submitter row */}
                                    <div className="apv-history-row">
                                        <span className="apv-history-date">{approvalHistory[0]?.CreatedDate}</span>
                                        <span className="apv-history-user">
                                            {approvalHistory[0]?.Submitted_by} <em>(Submitter)</em>
                                        </span>
                                        <span className="apv-history-status">Submit</span>
                                    </div>
                                    {/* Approver rows */}
                                    {approvalHistory.map((h, i) => {
                                        const isFinal = i === approvalHistory.length - 1;
                                        return (
                                            <div key={i} className="apv-history-row">
                                                <span className="apv-history-date">{h.CreatedDate}</span>
                                                <span className="apv-history-user">
                                                    {h.User} <em>({isFinal ? 'Final Approver' : 'Approver'})</em>
                                                </span>
                                                <span className={`apv-history-status apv-hist-${(h.Status || '').toLowerCase()}`}>
                                                    {h.Status}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ── Body ── */}
            <div className="shw-body">

                {/* Parties Section */}
                <div className="shw-parties">
                    {/* Company Column */}
                    <div className="shw-company-col">
                        <div className="shw-field-row"><span className="shw-field-label">Name:</span><span className="shw-field-val">{invoice.company_name}</span></div>
                        <div className="shw-field-row"><span className="shw-field-label">Address:</span><span className="shw-field-val">{invoice.company_address}</span></div>
                        <div className="shw-field-row"><span className="shw-field-label">Phone:</span><span>{invoice.company_phone}</span></div>
                        <div className="shw-field-row"><span className="shw-field-label">Email:</span><span>{invoice.company_email}</span></div>
                        <div className="shw-field-row"><span className="shw-field-label">Tax:</span><span>{invoice.company_tax_number}</span></div>
                        <div className="shw-field-row"><span className="shw-field-label">Pan Number:</span><span>{invoice.CompanyPanNumber}</span></div>
                    </div>

                    {/* Date Column */}
                    <div className="shw-date-col">
                        <div className="shw-date-row"><span>Date:</span><strong>{invoice.created_at}</strong></div>
                        <div className="shw-date-row"><span>Invoice No:</span><span>{invoice.InvoiceNumber}</span></div>
                    </div>

                    {/* Bill To Column */}
                    <div className="shw-billto-col">
                        <div className="shw-billto-title">Bill To</div>
                        <div className="shw-billto-name">{invoice.contact_name}</div>
                        <div className="shw-billto-sub">Tax Number: {invoice.contact_tax_number}</div>
                        <div className="shw-billto-sub">Pan Number: {invoice.ContactPanNumber}</div>
                        <div className="shw-billto-email">{invoice.contact_email}</div>
                        <div className="shw-billto-addr">
                            {[invoice.contact_address, invoice.BillToCity, invoice.BillToState, invoice.BillToCountry, invoice.BillToPincode].filter(Boolean).join(', ')}
                        </div>
                    </div>
                </div>

                {/* Items Table */}
                <div className="shw-items-section">
                    <table className="shw-items-table">
                        <thead>
                            <tr>
                                <th className="w40"></th>
                                <th>Items</th>
                                <th>Description</th>
                                <th>Quantity</th>
                                <th>Price</th>
                                <th>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, i) => (
                                <tr key={item.Id || i}>
                                    <td className="shw-grip">≡</td>
                                    <td><strong>{item.Name}</strong></td>
                                    <td className="shw-desc">{item.Description}</td>
                                    <td>{item.Quantity}</td>
                                    <td>{cur} {item.Price}</td>
                                    <td>{cur} {item.Total}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Totals */}
                <div className="shw-totals">
                    {otherCharges.map((c, i) => (
                        <div className="shw-total-row" key={i}>
                            <span>{c.Name}</span>
                            <span>{cur} {fmt(c.Amount)}</span>
                        </div>
                    ))}
                    <div className="shw-total-row">
                        <span>SUB TOTAL</span>
                        <span>{cur} {fmt(subTotal)}</span>
                    </div>
                    <div className="shw-total-row shw-grand">
                        <span>GRAND TOTAL</span>
                        <span>{cur} {fmt(grandTotal)}</span>
                    </div>
                </div>

                {/* Histories */}
                <div className="shw-history-section">
                    <div className="shw-history-header" onClick={() => setIsHistoryOpen(!isHistoryOpen)}>
                        <span>Histories</span>
                        {isHistoryOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                    {isHistoryOpen && (
                        <table className="shw-history-table">
                            <thead>
                                <tr>
                                    <th>DATE</th>
                                    <th>STATUS</th>
                                    <th>DESCRIPTION</th>
                                    <th>CREATED BY</th>
                                </tr>
                            </thead>
                            <tbody>
                                {histories.length === 0 ? (
                                    <tr><td colSpan="4" className="shw-no-history">No history records.</td></tr>
                                ) : (
                                    histories.map((h, i) => (
                                        <tr key={i}>
                                            <td>{h.CreatedOn}</td>
                                            <td>{h.Status}</td>
                                            <td>{h.Discription}</td>
                                            <td>{h.CreatedBy}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* ── Reject / Approve Modal ── */}
            {showRejectModal && (
                <div className="apv-modal-overlay" onClick={() => setShowRejectModal(false)}>
                    <div className="apv-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="apv-modal-header">
                            <h5 className="apv-modal-title">Approval/Rejection</h5>
                            <button
                                className="apv-modal-close"
                                onClick={() => setShowRejectModal(false)}
                                aria-label="Close"
                            >
                                &times;
                            </button>
                        </div>
                        <div className="apv-modal-body">
                            <div className="form-group">
                                <label className="form-control-label">Status</label>
                                <select
                                    className="form-control"
                                    value={rejectStatus}
                                    onChange={(e) => setRejectStatus(e.target.value)}
                                >
                                    <option value="Approved">Approved</option>
                                    <option value="Rejected">Rejected</option>
                                </select>
                            </div>
                            <div className="form-group" style={{ marginTop: 14 }}>
                                <label className="form-control-label">Remarks</label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    placeholder="Enter Description"
                                    value={rejectRemarks}
                                    onChange={(e) => setRejectRemarks(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="apv-modal-footer">
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setShowRejectModal(false)}
                            >
                                Close
                            </button>
                            <button
                                className="btn btn-success btn-sm"
                                onClick={handleSaveModal}
                                disabled={savingApproval}
                            >
                                {savingApproval ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShowCreditNoteView;
